import { Pagination } from "@/models/base/Pagination";
import { WorkspaceDocumentMetadata } from "@/models/metadata/metadata";
import RequestMethod from "../models/client/RequestMethod";
import RestResponse from "../models/client/RestResponse";
import Workspace, {
  AddOrUpdateWorkspaceDocumentResponseType,
  WorkspaceLinkRequest,
} from "../models/workspace/Workspace";
import {
  DocumentIndexStatus,
  RunningIndexOperation,
} from "../models/workspace/DocumentIndexStatus";
import { CrudServiceType, crudService } from "./crudService";
import { RestClient } from "./restClient";
import { FolderMetadataRecord } from "@/types/folder-metadata";
import {
  WorkspaceContentSearchRequest,
  WorkspaceContentSearchResult,
} from "@/models/workspace/WorkspaceContentSearch";

/**
 * Sort keys for the orphaned-workspaces endpoint. These are `WorkspaceViewModel` property
 * names — the API sorts by reflection, so they must match the C# properties exactly.
 */
/**
 * Outcome of a reindex request (LAR-252 async indexing): "completed" when
 * indexing finished within the call, "indexing" when a background run was
 * started or already in flight — poll getDocumentIndexStatus to observe it.
 */
export type ReindexDocumentResult = {
  status: "completed" | "indexing";
  operationId?: string | null;
};

export type OrphanedWorkspaceSortBy = "Name" | "CreatedAt";

export type DocumentRequestBody = {
  documentName: string;
  documentBlobName: string;
  documentSource: string;
  driveId?: string;
  siteId?: string;
  sharepointFileId?: string;
};

export type DocumentRequestBodyWithMetadataAndDocumentId =
  DocumentRequestBody & {
    documentId?: string;
    documentMetadataList?: WorkspaceDocumentMetadata[];
    folderId?: string;
  };

export type DocumentWithFolderRequestBody = DocumentRequestBody & {
  folderId?: string;
  documentMetadataList?: WorkspaceDocumentMetadata[];
};

export type SyncDocumentRequestBody = {
  newBlobName: string;
  newDocumentName: string;
};

export type DocumentBlobUrlResponse = {
  url: string;
};

export type PreviewPdfSheetItem = { name: string; index: number };

export type PreviewPdfSheetsResponse = {
  sheets: PreviewPdfSheetItem[];
};

export type WorkspaceDocumentQueryRequest = {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortAscending?: boolean;
  workspaceIds?: string[];
  metadataFilters?: Record<string, string[]>;
};

export type WorkspaceDocumentSearchItem = {
  documentId: string;
  name: string;
  workspaceId: string;
  workspaceName: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  documentType: string;
  documentMetadataList?: WorkspaceDocumentMetadata[];
  folderId?: string;
  folderName?: string;
};

export type WorkspaceDocumentSearchResponse = {
  data: WorkspaceDocumentSearchItem[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  hasMore: boolean;
};

export type DocumentMetadataFieldInfo = {
  fieldName: string;
  externalPropertyName?: string;
  distinctValues: string[];
};

export type WorkspaceServiceType = CrudServiceType<Workspace> & {
  getDocumentHtml: (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number
  ) => RestResponse<string>;
  getDocumentViewUrl: (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    isIndexed: boolean,
    parentTenantId?: string
  ) => RestResponse<DocumentBlobUrlResponse>;
  getDocumentPreviewPdfBlob: (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    parentTenantId?: string
  ) => RestResponse<Blob>;
  getDocumentPreviewPdfSheets: (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    parentTenantId?: string
  ) => RestResponse<PreviewPdfSheetsResponse>;
  getDocumentPreviewPdfSheetBlob: (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    sheetIndex: number,
    parentTenantId?: string
  ) => RestResponse<Blob>;
  getDocumentDownloadUrl: (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    isIndexed: boolean,
    parentTenantId?: string
  ) => RestResponse<DocumentBlobUrlResponse>;
  getAllWorkspaces: () => RestResponse<Workspace[]>;
  getWorkspaceById: (id: string) => RestResponse<Workspace>;
  searchWorkspaceDocuments: (
    requestBody: WorkspaceDocumentQueryRequest
  ) => RestResponse<WorkspaceDocumentSearchResponse>;
  /** Searches title + text of the user's workspace documents, grouped per workspace (LAR-235). */
  searchWorkspaceContent: (
    requestBody: WorkspaceContentSearchRequest
  ) => RestResponse<WorkspaceContentSearchResult>;
  addDocuments: (
    workspaceId: string,
    requestBody: {
      documents: DocumentRequestBodyWithMetadataAndDocumentId[];
      sessionId: string;
    }
  ) => RestResponse<AddOrUpdateWorkspaceDocumentResponseType>;
  moveDocuments: (
    workspaceId: string,
    requestBody: {
      newFolderId?: string;
      documentIds?: string[];
    }
  ) => RestResponse<void>;
  syncDocument: (
    workspaceId: string,
    documentId: string,
    requestBody: SyncDocumentRequestBody
  ) => RestResponse<Workspace>;
  removeDocument: (
    workspaceId: string,
    documentId: string
  ) => RestResponse<Workspace>;
  removeDocuments: (
    workspaceId: string,
    requestBody: {
      documentIds: string[];
    }
  ) => RestResponse<Workspace>;
  reindexDocument: (
    workspaceId: string,
    documentId: string,
    healOperationId?: string
  ) => RestResponse<ReindexDocumentResult>;
  getDocumentIndexStatus: (
    workspaceId: string,
    documentId: string
  ) => RestResponse<DocumentIndexStatus>;
  getRunningIndexOperations: (
    workspaceId: string
  ) => RestResponse<RunningIndexOperation[]>;
  grantAccess: (
    workspaceId: string,
    userIds: string[],
    role: string
  ) => RestResponse<Workspace>;
  revokeAccess: (
    workspaceId: string,
    userId: string
  ) => RestResponse<Workspace>;
  updateDocumentMetadata: (
    workspaceId: string,
    documentId: string,
    requestBody: {
      documentSource: string;
      driveId?: string;
      siteId?: string;
      sharepointFileId?: string;
      documentMetadataList?: WorkspaceDocumentMetadata[];
    }
  ) => RestResponse<Workspace>;
  renameDocument: (
    workspaceId: string,
    documentId: string,
    requestBody: {
      documentName: string;
    }
  ) => RestResponse<Workspace>;
  addFolder: (
    workspaceId: string,
    requestBody: {
      name?: string;
      parentFolderId?: string;
      paths?: string[];
      pathMetadataMap?: FolderMetadataRecord;
    }
  ) => RestResponse<Workspace>;
  updateFolder: (
    workspaceId: string,
    requestBody: {
      folderId: string;
      folderName: string;
    }
  ) => RestResponse<Workspace>;
  updateFolderMetadata: (
    workspaceId: string,
    folderId: string,
    requestBody: {
      folderSource: string;
      driveId?: string;
      siteId?: string;
      sharepointFileId?: string;
      name?: string;
    }
  ) => RestResponse<Workspace>;
  moveFolder: (
    worksapceId: string,
    requestBody: {
      folderId: string;
      newFolderLocation?: string;
    }
  ) => RestResponse<Workspace>;
  deleteFolder: (
    workspaceId: string,
    folderId: string
  ) => RestResponse<Workspace>;
  addLink: (
    workspaceId: string,
    requestBody: WorkspaceLinkRequest & { folderId?: string }
  ) => RestResponse<Workspace>;
  updateLink: (
    workspaceId: string,
    linkId: string,
    requestBody: WorkspaceLinkRequest
  ) => RestResponse<Workspace>;
  moveLink: (
    workspaceId: string,
    linkId: string,
    requestBody: {
      newFolderId?: string;
    }
  ) => RestResponse<Workspace>;
  deleteLink: (workspaceId: string, linkId: string) => RestResponse<Workspace>;
  changeActiveRevision: (
    workspaceId: string,
    documentId: string,
    requestBody: {
      newActiveRevisionVersion: number;
    }
  ) => RestResponse<Workspace>;
  getUploadStatus: (
    workspaceId: string,
    blobNames: string[]
  ) => RestResponse<{
    statuses: Record<
      string,
      {
        status: string;
        documentId?: string;
        documentName?: string;
        isRevision?: boolean;
      }
    >;
  }>;
  /** `pageNumber` is one-based, matching the API. */
  getOrphanedWorkspaces: (
    pageNumber: number,
    pageSize: number,
    search?: string,
    sortBy?: OrphanedWorkspaceSortBy,
    sortAscending?: boolean
  ) => RestResponse<Pagination<Workspace>>;
  claimWorkspace: (
    workspaceId: string,
    newOwnerId: string
  ) => RestResponse<void>;
  getDocumentMetadata: (
    workspaceIds?: string[]
  ) => RestResponse<DocumentMetadataFieldInfo[]>;
};

const workspaceServices = (
  client: RestClient,
  path: string
): WorkspaceServiceType => ({
  ...crudService<Workspace>(client, path),
  getDocumentHtml: async (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number
  ) => {
    const params = new URLSearchParams();
    params.append("blobId", blobId);
    params.append("revisionVersion", revisionVersion.toString());
    const url = `${path}/${workspaceId}/document/${documentId}/html?${params.toString()}`;
    return await client.fetch(RequestMethod.GET, url);
  },
  getDocumentViewUrl: async (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    isIndexed: boolean,
    parentTenantId?: string
  ) => {
    const params = new URLSearchParams();
    params.append("blobId", blobId);
    params.append("revisionVersion", revisionVersion.toString());
    params.append("isIndexed", isIndexed.toString());
    if (parentTenantId) params.append("parentTenantId", parentTenantId);
    const url = `${path}/${workspaceId}/document/${documentId}/view-url?${params.toString()}`;
    return await client.fetch<DocumentBlobUrlResponse>(RequestMethod.GET, url);
  },
  getDocumentPreviewPdfBlob: async (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    parentTenantId?: string
  ) => {
    const params = new URLSearchParams();
    params.append("blobId", blobId);
    params.append("revisionVersion", revisionVersion.toString());
    if (parentTenantId) params.append("parentTenantId", parentTenantId);
    const url = `${path}/${workspaceId}/document/${documentId}/preview-pdf?${params.toString()}`;
    return await client.fetchAsBlob(RequestMethod.GET, url);
  },
  getDocumentPreviewPdfSheets: async (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    parentTenantId?: string
  ) => {
    const params = new URLSearchParams();
    params.append("blobId", blobId);
    params.append("revisionVersion", revisionVersion.toString());
    if (parentTenantId) params.append("parentTenantId", parentTenantId);
    const url = `${path}/${workspaceId}/document/${documentId}/preview-pdf/sheets?${params.toString()}`;
    return await client.fetch<PreviewPdfSheetsResponse>(RequestMethod.GET, url);
  },
  getDocumentPreviewPdfSheetBlob: async (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    sheetIndex: number,
    parentTenantId?: string
  ) => {
    const params = new URLSearchParams();
    params.append("blobId", blobId);
    params.append("revisionVersion", revisionVersion.toString());
    if (parentTenantId) params.append("parentTenantId", parentTenantId);
    const url = `${path}/${workspaceId}/document/${documentId}/preview-pdf/sheets/${sheetIndex}?${params.toString()}`;
    return await client.fetchAsBlob(RequestMethod.GET, url);
  },
  getDocumentDownloadUrl: async (
    workspaceId: string,
    documentId: string,
    blobId: string,
    revisionVersion: number,
    isIndexed: boolean,
    parentTenantId?: string
  ) => {
    const params = new URLSearchParams();
    params.append("blobId", blobId);
    params.append("revisionVersion", revisionVersion.toString());
    params.append("isIndexed", isIndexed.toString());
    if (parentTenantId) params.append("parentTenantId", parentTenantId);
    const url = `${path}/${workspaceId}/document/${documentId}/download-url?${params.toString()}`;
    return await client.fetch<DocumentBlobUrlResponse>(RequestMethod.GET, url);
  },
  getAllWorkspaces: async () =>
    await client.fetch(RequestMethod.GET, `${path}/all`),
  getWorkspaceById: async (id) =>
    await client.fetch(RequestMethod.GET, `${path}/${id}`),
  searchWorkspaceDocuments: async (
    requestBody: WorkspaceDocumentQueryRequest
  ) =>
    await client.fetch(
      RequestMethod.POST,
      `${path}/documents/query`,
      requestBody
    ),
  searchWorkspaceContent: async (requestBody: WorkspaceContentSearchRequest) =>
    await client.fetch<WorkspaceContentSearchResult>(
      RequestMethod.POST,
      `${path}/documents/search-content`,
      requestBody
    ),
  addDocuments: async (
    workspaceId: string,
    requestBody: {
      documents: DocumentRequestBodyWithMetadataAndDocumentId[];
      sessionId: string;
    }
  ) =>
    await client.fetch(
      RequestMethod.POST,
      `${path}/${workspaceId}/documents`,
      requestBody
    ),
  moveDocuments: async (
    workspaceId: string,
    requestBody: {
      newFolderId?: string;
      documentIds?: string[];
    }
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/documents/move`,
      requestBody
    ),
  syncDocument: async (
    workspaceId: string,
    documentId: string,
    requestBody: SyncDocumentRequestBody
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/document/${documentId}/sync`,
      requestBody
    ),

  removeDocument: async (workspaceId: string, documentId: string) =>
    await client.fetch(
      RequestMethod.DELETE,
      `${path}/${workspaceId}/document/${documentId}`
    ),
  removeDocuments: async (
    workspaceId: string,
    requestBody: {
      documentIds: string[];
    }
  ) =>
    await client.fetch(
      RequestMethod.DELETE,
      `${path}/${workspaceId}/documents`,
      requestBody.documentIds
    ),

  reindexDocument: async (
    workspaceId: string,
    documentId: string,
    healOperationId?: string
  ) =>
    await client.fetch(
      RequestMethod.POST,
      `${path}/${workspaceId}/document/${documentId}/reindex${
        healOperationId
          ? `?healOperationId=${encodeURIComponent(healOperationId)}`
          : ""
      }`
    ),

  getDocumentIndexStatus: async (workspaceId: string, documentId: string) =>
    await client.fetch(
      RequestMethod.GET,
      `${path}/${workspaceId}/document/${documentId}/index-status`
    ),

  getRunningIndexOperations: async (workspaceId: string) =>
    await client.fetch(
      RequestMethod.GET,
      `${path}/${workspaceId}/index-operations/running`
    ),

  grantAccess: async (workspaceId: string, userIds: string[], role: string) =>
    await client.fetch(RequestMethod.POST, `${path}/${workspaceId}/access`, {
      userIds,
      role,
    }),

  revokeAccess: async (workspaceId: string, userId: string) =>
    await client.fetch(RequestMethod.DELETE, `${path}/${workspaceId}/access`, {
      userId,
    }),

  updateDocumentMetadata: async (
    workspaceId: string,
    documentId: string,
    requestBody: {
      documentSource: string;
      driveId?: string;
      siteId?: string;
      sharepointFileId?: string;
      documentMetadataList?: WorkspaceDocumentMetadata[];
    }
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/document/${documentId}/metadata`,
      requestBody
    ),
  renameDocument: async (
    workspaceId: string,
    documentId: string,
    requestBody: {
      documentName: string;
    }
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/document/${documentId}/name`,
      requestBody
    ),
  addFolder: async (
    workspaceId: string,
    requestBody: {
      name?: string;
      parentFolderId?: string;
      paths?: string[];
      pathMetadataMap?: FolderMetadataRecord;
    }
  ) =>
    await client.fetch(
      RequestMethod.POST,
      `${path}/${workspaceId}/folder`,
      requestBody
    ),
  updateFolder: async (
    workspaceId: string,
    requestBody: {
      folderId: string;
      folderName: string;
    }
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/folder`,
      requestBody
    ),
  updateFolderMetadata: async (
    workspaceId: string,
    folderId: string,
    requestBody: {
      folderSource: string;
      driveId?: string;
      siteId?: string;
      sharepointFileId?: string;
      name?: string;
    }
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/folder/${folderId}/metadata`,
      requestBody
    ),
  moveFolder: async (
    workspaceId: string,
    requestBody: {
      folderId: string;
      newFolderLocation?: string;
    }
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/folder/move`,
      requestBody
    ),
  deleteFolder: async (workspaceId: string, folderId: string) =>
    await client.fetch(
      RequestMethod.DELETE,
      `${path}/${workspaceId}/folder/${folderId}`
    ),
  addLink: async (
    workspaceId: string,
    requestBody: WorkspaceLinkRequest & { folderId?: string }
  ) =>
    await client.fetch(
      RequestMethod.POST,
      `${path}/${workspaceId}/link`,
      requestBody
    ),
  updateLink: async (
    workspaceId: string,
    linkId: string,
    requestBody: WorkspaceLinkRequest
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/link/${linkId}`,
      requestBody
    ),
  moveLink: async (
    workspaceId: string,
    linkId: string,
    requestBody: {
      newFolderId?: string;
    }
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/link/${linkId}/move`,
      requestBody
    ),
  deleteLink: async (workspaceId: string, linkId: string) =>
    await client.fetch(
      RequestMethod.DELETE,
      `${path}/${workspaceId}/link/${linkId}`
    ),
  changeActiveRevision: async (
    workspaceId: string,
    documentId: string,
    requestBody: {
      newActiveRevisionVersion: number;
    }
  ) =>
    await client.fetch(
      RequestMethod.PUT,
      `${path}/${workspaceId}/document/${documentId}/active-revision`,
      requestBody
    ),
  getUploadStatus: async (workspaceId: string, blobNames: string[]) => {
    const queryParams = new URLSearchParams();
    blobNames.forEach((blobName) => {
      queryParams.append("blobNames", blobName);
    });
    return await client.fetch(
      RequestMethod.GET,
      `${path}/${workspaceId}/uploads/status?${queryParams.toString()}`
    );
  },
  getOrphanedWorkspaces: async (
    pageNumber: number,
    pageSize: number,
    search?: string,
    sortBy: OrphanedWorkspaceSortBy = "Name",
    sortAscending: boolean = true
  ) => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
    return await client.fetch<Pagination<Workspace>>(
      RequestMethod.GET,
      `${path}/orphaned?pageNumber=${pageNumber}&pageSize=${pageSize}${searchParam}` +
        `&sortBy=${sortBy}&sortAscending=${sortAscending}`
    );
  },
  claimWorkspace: async (workspaceId: string, newOwnerId: string) =>
    await client.fetch(RequestMethod.POST, `${path}/${workspaceId}/claim`, {
      newOwnerId,
    }),
  getDocumentMetadata: async (workspaceIds?: string[]) => {
    const params = new URLSearchParams();
    workspaceIds?.forEach((id) => params.append("workspaceIds", id));
    const query = params.toString();
    return await client.fetch(
      RequestMethod.GET,
      `${path}/documents/metadata${query ? `?${query}` : ""}`
    );
  },
});

export default workspaceServices;
